import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, ValidatorFn, Validators } from '@angular/forms';

import { ValidatorsService } from '@connect-services/client/services/validators.service';
import { InformationSchemaTableExtended } from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class AttributeValidationService {
    public readonly errorKeyToMessageKey = new Map<string, string>([
        ['maxlength', 'maxLengthErrorMessage'],
        ['max', 'maxValueErrorMessage'],
        ['notInteger', 'notIntegerErrorMessage'],
        ['notPositiveNumber', 'notPositiveNumberErrorMessage'],
        ['invalidJson', 'jsonNotValidErrorMessage'],
        ['matDatepickerParse', 'dateNotValidErrorMessage'],
        ['required', 'requiredField'],
    ]);

    public buildValidators(information: InformationSchemaTableExtended): ValidatorFn[] {
        const validators: ValidatorFn[] = [];

        if (information?.isNullable === false) {
            validators.push(Validators.required);
        }

        if (!information) {
            return validators;
        }

        if (information.maximumLength) {
            if (information.dataType === InformationSchemaTableExtended.DataTypeEnum.STRING) {
                validators.push(Validators.maxLength(information.maximumLength));
            } else {
                validators.push(Validators.max(information.maximumLength));
            }
        }

        if (information.dataType === InformationSchemaTableExtended.DataTypeEnum.INTEGER) {
            validators.push(ValidatorsService.validateInteger);
        }

        if (information.dataType === InformationSchemaTableExtended.DataTypeEnum.REAL) {
            validators.push(ValidatorsService.validatePositiveNumber);
        }

        if (information.dataType === InformationSchemaTableExtended.DataTypeEnum.JSON) {
            validators.push(ValidatorsService.validateJson);
        }

        if (information.dataType === InformationSchemaTableExtended.DataTypeEnum.DATE) {
            validators.push(ValidatorsService.validateDate());
        }

        return validators;
    }

    public createControl(value: unknown, info: InformationSchemaTableExtended): FormControl<unknown | null> {
        const control = new FormControl(value, {
            nonNullable: !info?.isNullable,
            validators: this.buildValidators(info),
        });

        if (!info) {
            return control;
        }

        if (info.dataType !== InformationSchemaTableExtended.DataTypeEnum.DATE) {
            return control;
        }

        if (control.value === null || control.value === '') {
            return control;
        }

        const parsed = this.normalizeToDate(control.value);

        if (!parsed || Number.isNaN(parsed.getTime())) {
            control.setErrors({
                ...(control.errors ?? {}),
                matDatepickerParse: true,
            });
        }

        return control;
    }

    public getErrorMessages(control: AbstractControl<unknown> | null): string[] {
        if (!control?.errors) {
            return [];
        }

        return Object.keys(control.errors)
            .map((key) => this.errorKeyToMessageKey.get(key))
            .filter((msgKey): msgKey is string => !!msgKey);
    }

    public isNumericType(info?: InformationSchemaTableExtended | null): boolean {
        if (!info) {
            return false;
        }

        return [
            InformationSchemaTableExtended.DataTypeEnum.REAL,
            InformationSchemaTableExtended.DataTypeEnum.INTEGER,
            InformationSchemaTableExtended.DataTypeEnum.FLOAT,
        ].includes(info.dataType);
    }

    private normalizeToDate(raw: unknown): Date | null {
        if (raw instanceof Date) {
            return raw;
        }

        if (typeof raw === 'string') {
            const date = new Date(raw);

            return Number.isNaN(date.getTime()) ? null : date;
        }

        return null;
    }
}
